import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
// Import the interactive diagram component
import InteractiveDiagram from "../../components/InteractiveDiagram";

const IAM = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>AWS Identity and Access Management (IAM) - limkc</title>
        <meta name="description" content="Learn about AWS Identity and Access Management (IAM) with real-world examples, implementation strategies, and decision-making flowcharts for securing your AWS resources." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={window.location.href} />
      </Helmet>
  
      <div className="w-full h-full overflow-auto">
        {/* Banner Image - Full Width */}
        <div className="w-full h-48 overflow-hidden" style={{ marginTop: 'var(--navbar-height)' }}>
          <img
            src="/IMAGE/iam.png"
            alt="AWS Identity and Access Management"
            className="w-full h-full object-cover object-center"
          />
        </div>

        {/* Content - Centered */}
        <div className="w-full flex justify-center">
          <div className="py-6 transition-all duration-300 w-full"
            style={{
              backgroundColor: 'transparent',
              color: 'var(--text-primary)',
              maxWidth: '1200px',
              paddingLeft: '1rem',
              paddingRight: '1rem'
            }}>

            <h1 className="text-3xl font-bold">AWS Identity and Access Management (IAM)</h1>
          <p className="mt-4" style={{ color: 'var(--text-secondary)' }}>
            AWS IAM enables secure control over access to AWS resources. It allows you to define who (users, groups, or applications) 
            can do what (actions like read, write, delete) on which AWS resources (e.g., S3 buckets, EC2 instances). 
            IAM handles both authentication (verifying identity) and authorization (granting permissions).
          </p>
  
          <h2 className="text-2xl font-semibold mt-6">Why IAM is Critical</h2>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            Proper IAM configuration is essential for AWS security and resource management.
          </p>
          <ul className="list-disc list-inside mt-2" style={{ color: 'var(--text-secondary)' }}>
            <li>Security – Control who can access specific AWS resources with fine-grained permissions.</li>
            <li>Compliance – Meet regulatory requirements with audit trails and least privilege access.</li>
            <li>Scalability – Manage access for teams of any size using groups and roles.</li>
            <li>Flexibility – Support various authentication methods from direct users to federated identity.</li>
          </ul>
  
          <h2 className="text-2xl font-semibold mt-6">IAM Decision Flowchart</h2>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            Choosing the right IAM approach depends on your specific needs. The interactive diagram below illustrates a 
            decision-making process to help determine the optimal IAM setup for your organization:
          </p>
          
          <div className="relative my-4 max-h-[50vh] md:max-h-[720px] overflow-auto">
            {/* Full-size diagram */}
            <InteractiveDiagram 
              name="iam-flowchart" 
              alt="AWS IAM Decision Flowchart" 
              className="w-full h-full"
            />
            <p className="text-xs text-center text-gray-500 mt-1">
              AWS IAM decision-making flowchart
              <br />
              <span className="text-xs text-gray-300">© 2025 limkc. All rights reserved. Reproduction requires attribution.</span>
            </p>
          </div>
          
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            This flowchart guides decision-making based on several key factors:
          </p>
          <ul className="list-disc list-inside mt-2" style={{ color: 'var(--text-secondary)' }}>
            <li><span className="text-blue-500 font-semibold">Team size</span> - Small teams vs. large organizations</li>
            <li><span className="text-green-500 font-semibold">Existing systems</span> - Whether you already have identity providers</li>
            <li><span className="text-orange-500 font-semibold">User types</span> - Internal employees vs. external partners</li>
          </ul>
  
          <h2 className="text-2xl font-semibold mt-6">Core IAM Components</h2>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            AWS IAM consists of several interconnected components that work together to provide secure access management.
          </p>
  
          <div className="bg-blue-50 p-4 rounded-lg mt-4">
            <h3 className="text-xl font-semibold text-blue-700">Key IAM Components</h3>
            <div className="grid md:grid-cols-2 gap-4 mt-3">
              <div>
                <h4 className="font-semibold text-blue-800">IAM Users</h4>
                <p className="text-gray-600 text-sm mt-1">
                  Individual identities created in AWS that represent people or services. Each user has unique security credentials
                  and can be assigned permissions directly or through group membership.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-blue-800">IAM Groups</h4>
                <p className="text-gray-600 text-sm mt-1">
                  Collections of IAM users that share the same permissions. Simplifies management by allowing you to assign
                  permissions to multiple users at once instead of applying them individually.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-blue-800">IAM Roles</h4>
                <p className="text-gray-600 text-sm mt-1">
                  Sets of permissions that can be assumed by AWS services, applications, or federated users. Roles provide
                  temporary credentials and are ideal for cross-account access and service-to-service communication.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-blue-800">IAM Policies</h4>
                <p className="text-gray-600 text-sm mt-1">
                  JSON documents that define permissions. Policies can be attached to users, groups, or roles and specify
                  what actions are allowed or denied on which AWS resources under what conditions.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-blue-800">Identity Providers</h4>
                <p className="text-gray-600 text-sm mt-1">
                  External systems that manage user identities and can be federated with AWS IAM. Examples include Active Directory,
                  Okta, Google Workspace, and other SAML or OpenID Connect compatible providers.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-blue-800">Multi-Factor Authentication</h4>
                <p className="text-gray-600 text-sm mt-1">
                  Adds an extra layer of security by requiring users to provide a second form of authentication in addition
                  to their password. This significantly enhances security for sensitive AWS operations.
                </p>
              </div>
            </div>
          </div>
  
          <h3 className="text-xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>When to Choose Internal vs. External Identity</h3>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            Deciding between internal IAM users/groups and external federation depends on your organization's structure and needs:
          </p>
          
          <div className="grid md:grid-cols-2 gap-6 mt-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold text-gray-800">Internal: IAM Users & Groups</h4>
              <p className="text-gray-600 text-sm mt-2">Best for:</p>
              <ul className="list-disc list-inside text-gray-600 text-sm mt-1">
                <li>Small teams (1-20 users)</li>
                <li>Organizations without existing identity systems</li>
                <li>Simple projects with limited AWS accounts</li>
                <li>Environments needing fine-grained AWS-specific control</li>
              </ul>
              <p className="text-gray-600 text-sm mt-2">Advantages:</p>
              <ul className="list-disc list-inside text-gray-600 text-sm mt-1">
                <li>Full control within AWS—no external dependencies</li>
                <li>Simpler setup and maintenance</li>
                <li>Direct management of access keys and passwords</li>
              </ul>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold text-gray-800">External: Identity Provider Federation</h4>
              <p className="text-gray-600 text-sm mt-2">Best for:</p>
              <ul className="list-disc list-inside text-gray-600 text-sm mt-1">
                <li>Large organizations (50+ users)</li>
                <li>Teams already using identity providers (Okta, AD, etc.)</li>
                <li>Multi-cloud or hybrid environments</li>
                <li>External partners requiring temporary access</li>
              </ul>
              <p className="text-gray-600 text-sm mt-2">Advantages:</p>
              <ul className="list-disc list-inside text-gray-600 text-sm mt-1">
                <li>Single Sign-On (SSO) across multiple systems</li>
                <li>Centralized identity management</li>
                <li>Reduced credential management overhead</li>
                <li>Simplified user lifecycle management</li>
              </ul>
            </div>
          </div>
  
          <h3 className="text-xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>IAM Policy Structure</h3>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            IAM policies define permissions using a structured JSON format. Understanding policy components is essential for implementing
            secure access controls:
          </p>
  
          <pre className="p-4 rounded-md mt-2 text-sm overflow-x-auto" style={{ backgroundColor: 'var(--bg-dark)', color: 'var(--text-on-dark)' }}>
{`{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Sid": "AllowS3BucketAccess",
      "Effect": "Allow",
      "Action": [
        "s3:GetObject",
        "s3:ListBucket"
      ],
      "Resource": [
        "arn:aws:s3:::example-bucket",
        "arn:aws:s3:::example-bucket/*"
      ],
      "Condition": {
        "IpAddress": {
          "aws:SourceIp": "192.0.2.0/24"
        }
      }
    }
  ]
}`}
          </pre>
  
          <p className="mt-3" style={{ color: 'var(--text-secondary)' }}>
            Policy elements explained:
          </p>
          <ul className="list-disc list-inside mt-2" style={{ color: 'var(--text-secondary)' }}>
            <li><code className="px-1 rounded" style={{ backgroundColor: 'var(--bg-dark)', color: 'var(--text-on-dark)' }}>Version</code> - Policy language version (almost always "2012-10-17")</li>
            <li><code className="px-1 rounded" style={{ backgroundColor: 'var(--bg-dark)', color: 'var(--text-on-dark)' }}>Statement</code> - Container for permission statements</li>
            <li><code className="px-1 rounded" style={{ backgroundColor: 'var(--bg-dark)', color: 'var(--text-on-dark)' }}>Sid</code> - Optional identifier for the statement</li>
            <li><code className="px-1 rounded" style={{ backgroundColor: 'var(--bg-dark)', color: 'var(--text-on-dark)' }}>Effect</code> - Whether to allow or deny the permissions</li>
            <li><code className="px-1 rounded" style={{ backgroundColor: 'var(--bg-dark)', color: 'var(--text-on-dark)' }}>Action</code> - List of actions the policy allows or denies</li>
            <li><code className="px-1 rounded" style={{ backgroundColor: 'var(--bg-dark)', color: 'var(--text-on-dark)' }}>Resource</code> - The AWS resources the actions apply to</li>
            <li><code className="px-1 rounded" style={{ backgroundColor: 'var(--bg-dark)', color: 'var(--text-on-dark)' }}>Condition</code> - Optional conditions for when the policy applies</li>
          </ul>
  
          <h2 className="text-2xl font-semibold mt-6">Implementation Scenarios</h2>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            Let's explore common IAM implementation patterns for different organization types:
          </p>
          
          <h3 className="text-xl font-semibold mt-4">Scenario 1: Small Startup (5 Developers)</h3>
          <div className="bg-gray-50 p-4 rounded-lg mt-2">
            <p className="text-gray-600 text-sm">
              <strong>Need:</strong> Developers need access to S3 buckets and EC2 instances.
            </p>
            <p className="text-gray-600 text-sm mt-2">
              <strong>Best Choice:</strong> Internal IAM Users + Groups
            </p>
            <p className="text-gray-600 text-sm mt-2">
              <strong>Implementation:</strong>
            </p>
            <ol className="list-decimal list-inside text-gray-600 text-sm mt-1">
              <li>Create IAM users for each developer (e.g., "dev1", "dev2")</li>
              <li>Create a "Developers" group and add all developers to it</li>
              <li>Attach AWS-managed policies "AmazonS3ReadWrite" and "AmazonEC2ContainerRegistryPowerUser" to the group</li>
              <li>Enforce MFA for all users</li>
            </ol>
          </div>
          
          <h3 className="text-xl font-semibold mt-4">Scenario 2: Mid-Sized Company (50 Employees, Using Okta)</h3>
          <div className="bg-gray-50 p-4 rounded-lg mt-2">
            <p className="text-gray-600 text-sm">
              <strong>Need:</strong> Employees across departments need varying AWS access, and they already use Okta for other tools.
            </p>
            <p className="text-gray-600 text-sm mt-2">
              <strong>Best Choice:</strong> External Identity Provider Federation
            </p>
            <p className="text-gray-600 text-sm mt-2">
              <strong>Implementation:</strong>
            </p>
            <ol className="list-decimal list-inside text-gray-600 text-sm mt-1">
              <li>Configure Okta as a SAML Identity Provider in IAM</li>
              <li>Create IAM roles for different departments (e.g., "DevRole", "FinanceRole")</li>
              <li>Set up role trust policies that allow Okta to assume these roles</li>
              <li>Map Okta groups to corresponding AWS roles</li>
              <li>Configure custom session durations based on security requirements</li>
            </ol>
          </div>
          
          <h3 className="text-xl font-semibold mt-4">Scenario 3: CI/CD Pipeline Automation</h3>
          <div className="bg-gray-50 p-4 rounded-lg mt-2">
            <p className="text-gray-600 text-sm">
              <strong>Need:</strong> Jenkins server needs to deploy applications to AWS services.
            </p>
            <p className="text-gray-600 text-sm mt-2">
              <strong>Best Choice:</strong> IAM Roles for EC2 instances
            </p>
            <p className="text-gray-600 text-sm mt-2">
              <strong>Implementation:</strong>
            </p>
            <ol className="list-decimal list-inside text-gray-600 text-sm mt-1">
              <li>Create an IAM role with necessary deployment permissions</li>
              <li>Set up a trust policy allowing EC2 to assume the role</li>
              <li>Launch Jenkins on EC2 with this IAM role attached</li>
              <li>The Jenkins server automatically gets temporary credentials without storing access keys</li>
            </ol>
          </div>
  
          <h2 className="text-2xl font-semibold mt-6">IAM Best Practices</h2>
          <ul className="list-disc list-inside mt-2" style={{ color: 'var(--text-secondary)' }}>
            <li>Apply the principle of least privilege – grant only the permissions needed</li>
            <li>Use IAM groups to assign permissions to users</li>
            <li>Enable MFA for all users, especially those with administrative access</li>
            <li>Rotate credentials regularly and enforce strong password policies</li>
            <li>Use roles for applications and federated users instead of hard-coded credentials</li>
            <li>Leverage AWS-managed policies where possible for standardization</li>
            <li>Monitor IAM activity using AWS CloudTrail</li>
            <li>Regularly audit permissions using IAM Access Analyzer</li>
            <li>Implement emergency access procedures for break-glass scenarios</li>
          </ul>
  
          <h2 className="text-2xl font-semibold mt-6">Common Challenges & Solutions</h2>
          <ul className="list-disc list-inside mt-2" style={{ color: 'var(--text-secondary)' }}>
            <li><b>Permission Sprawl</b> – Use IAM Access Advisor to identify unused permissions.</li>
            <li><b>Secret Management</b> – Leverage AWS Secrets Manager instead of embedding keys.</li>
            <li><b>Cross-Account Access</b> – Use role assumption with external IDs for secure cross-account operations.</li>
            <li><b>Identity Lifecycle</b> – Automate user provisioning/de-provisioning through identity federation.</li>
            <li><b>Permission Boundaries</b> – Implement these to limit maximum permissions for delegated administration.</li>
            <li><b>Service Control Policies</b> – Apply organization-wide guardrails in AWS Organizations.</li>
            <li><b>Credential Leakage</b> – Use tools like AWS Config to detect publicly exposed keys.</li>
          </ul>
  
          <h2 className="text-2xl font-semibold mt-6">Real World Experience</h2>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            I implemented AWS IAM for a company during their cloud migration. Key challenges included meeting strict compliance requirements, 
            managing access for hundreds of developers, and integrating with their existing identity provider.
          </p>
  
          <p className="mt-3" style={{ color: 'var(--text-secondary)' }}>
            Key lessons from implementation:
          </p>
          <ul className="list-disc list-inside mt-2" style={{ color: 'var(--text-secondary)' }}>
            <li>Starting with overly broad permissions required painful remediation later; always begin with least privilege</li>
            <li>Identity federation saved approximately 20 hours of administrative work per week compared to managing individual IAM users</li>
            <li>Using permission boundaries allowed delegation of IAM administration while maintaining security guardrails</li>
            <li>Implementing automated compliance checks with AWS Config reduced audit preparation time by 70%</li>
          </ul>
  
          {/* <h2 className="text-2xl font-semibold mt-6">Related Topics</h2>
          <ul className="list-disc list-inside mt-2" style={{ color: 'var(--text-secondary)' }}>
            <li><a href="/topics/cloud-security" className="text-blue-500 hover:underline">Cloud Security & Compliance</a></li>
            <li><a href="/topics/aws-organizations" className="text-blue-500 hover:underline">AWS Organizations & Control Tower</a></li>
            <li><a href="/topics/zero-trust" className="text-blue-500 hover:underline">Zero Trust Architecture</a></li>
            <li><a href="/topics/cloud-security" className="text-blue-500 hover:underline">Cloud Security & Compliance</a></li>
            <li><a href="/topics/aws-organizations" className="text-blue-500 hover:underline">AWS Organizations & Control Tower</a></li>
            <li><a href="/topics/zero-trust" className="text-blue-500 hover:underline">Zero Trust Architecture</a></li>
          </ul> */}
  
          <button 
            onClick={() => navigate("/projects")} 
            className="mt-6 px-4 py-2 rounded-lg transition-colors"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              border: '1px solid var(--text-secondary)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
            }}
          >
            ← Back to Topics
          </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default IAM;  //IAM.JS